import { useEffect, useRef } from 'react';
import { useAppDispatch } from './useAppDispatch';
import { useAppSelector } from './useAppSelector';
import { resetRange } from '../store/slice/date-slice';
import {
  clearCurrencyData,
  fetchStats,
  resetTableFooter,
  setSelectedGroupBy,
  setShouldExpandSum,
} from '../store/slice/stats-slice';
import { StatsFetchType } from '../enum/StatsFetchType';
import { setShowColumnOrder } from '../store/slice/global-filters';
import { buildRequestForDeepestGeoLevel } from '../util/buildRequestForDeepestGeoLevel';
import { getFinalUTCDateRange } from './useFormatDateTime';
import { resolveGroupBy } from '../util/group-by-helper';
import { resolveGroupByFromColumns } from '../util/resolveGroupBy';
import { isGeoColumn } from '../util/table-helper';
import { STATISTIC_PAGE } from '../constant/constants';
import { StatsGroupEnum } from '../enum/StatsGroupEnum';

/**
 * useStatsFetch
 * ----------------------------------------------------
 * Performs EXACTLY ONE initial stats fetch on mount / on
 * arriving at the Statistics page - it does not listen for
 * filter or groupBy changes (that's now handled exclusively
 * by handleApplyAllFilters, triggered by the Search/Update
 * data/Sum By buttons).
 *
 * Waits for geoFiltersData to arrive from the backend/API
 * before firing the initial fetch. columns are used
 * OPPORTUNISTICALLY to resolve groupBy when already available
 * (e.g. on a subsequent visit within the same session), but are
 * NOT required to fire the fetch - columns for the Statistics
 * page are only populated from the stats response itself, so
 * gating on columns would deadlock the very first fetch after a
 * fresh login (columns never arrive because the fetch that
 * would produce them never runs).
 */
export const useStatsFetch = () => {
  const dispatch = useAppDispatch();
  const fetchedRef = useRef(false);

  const pageKey = useAppSelector((state) => state.configSlice.activePageKey);
  const columns = useAppSelector((state) => state.configSlice.columnsByPage[pageKey]);
  const geoFiltersData = useAppSelector((state) => state.filters.geoFiltersData);
  const dateFilter = useAppSelector((state) => state.dateFilter);
  const timeZone = useAppSelector((state) => state.configSlice.timeZone);
  const globalFilters = useAppSelector((state) => state.globalFilters);
  const selectedCurrency = useAppSelector((state) => state.configSlice.selectedCurrency);

  /* --------------------------------------------------
   * Reset range/footer runs immediately on mount,
   * it does not wait for columns/geoFiltersData.
   * -------------------------------------------------- */
  useEffect(() => {
    dispatch(resetRange());
    dispatch(resetTableFooter());
    // eslint-disable-next-line
  }, [dispatch]);

  /* --------------------------------------------------
   * Whenever the active page changes, allow a fresh
   * initial fetch for that page.
   * -------------------------------------------------- */
  useEffect(() => {
    fetchedRef.current = false;
  }, [pageKey, timeZone, selectedCurrency]);

  /* --------------------------------------------------
   * Initial fetch - only ONCE per arrival on the page,
   * and only once geoFiltersData has actually arrived.
   * columns are consulted opportunistically (see comment
   * above) but never block the initial fetch.
   * -------------------------------------------------- */
  useEffect(() => {
    if (pageKey !== STATISTIC_PAGE) return;
    if (fetchedRef.current) return; // fetch only once per page visit
    if (!geoFiltersData || Object.keys(geoFiltersData).length === 0) return; // wait for geo filters

    fetchedRef.current = true;

    /**
     * The geo level is derived from the current (backend) column
     * visibility - this is the same resolver used by
     * handleApplyAllFilters, so the initial fetch stays consistent
     * with the fetch triggered by a button click. Only attempt this
     * when columns are already known; otherwise fall back to
     * geoGroup ?? 0 below instead of blocking the fetch. */
    const geoColumnsOrdered = Object.keys(geoFiltersData).filter(isGeoColumn);
    const geoGroup = columns && columns.length > 0 ? resolveGroupByFromColumns(geoColumnsOrdered, columns) : undefined;

    if (geoGroup !== undefined) {
      dispatch(setSelectedGroupBy(geoGroup)); // sync it so the UI/later reads see the right value
    }

    const selectedDevices = globalFilters.PaymentSource || [];
    const selectedPaymentTypes = globalFilters.PaymentType || [];

    const { groupBy: finalGroupBy } = resolveGroupBy({
      geoGroup: geoGroup ?? StatsGroupEnum.Location,
      selectedDevices,
      selectedPaymentTypes,
      shouldSum: globalFilters.shouldSumByDevice,
    });

    const { startDate, endDate } = getFinalUTCDateRange(dateFilter, timeZone);

    const fetchData = async () => {
      try {
        await dispatch(
          fetchStats({
            type: StatsFetchType.STATS,
            request: {
              ...buildRequestForDeepestGeoLevel(globalFilters),
              Date: { StartDate: startDate, EndDate: endDate },
              CCY: selectedCurrency,
              groupBy: finalGroupBy,
            },
          }),
        );
        if (selectedCurrency) {
          dispatch(setShouldExpandSum(true));
          dispatch(clearCurrencyData());
          await dispatch(
            fetchStats({
              type: StatsFetchType.EXPAND_SUM,
              request: {
                ...buildRequestForDeepestGeoLevel(globalFilters),
                Date: { StartDate: startDate, EndDate: endDate },
                groupBy: [StatsGroupEnum.CCY],
              },
            }),
          );

          await dispatch(
            fetchStats({
              type: StatsFetchType.EXPAND_SUM_ITEM,
              request: {
                ...buildRequestForDeepestGeoLevel(globalFilters),
                Date: { StartDate: startDate, EndDate: endDate },
                groupBy: [StatsGroupEnum.CCY],
                CCY: selectedCurrency,
              },
            }),
          );
        }
      } catch (err) {
        console.error('Failed to fetch stats:', err);
      }
    };

    fetchData();
    dispatch(setShowColumnOrder(true));
    // eslint-disable-next-line
  }, [columns, geoFiltersData, pageKey, selectedCurrency, timeZone]);
};
